import {EtapaService} from './../shared/service/etapa-service';
import {ChangeDetectorRef, Component, inject, OnInit, ViewChild} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {FCanvasChangeEvent, FCanvasComponent, FFlowModule} from '@foblex/flow';
import {ActivatedRoute} from '@angular/router';
import {EtapaBusca} from '../shared/model/etapa/etapa-busca-model';
import {FluxoService} from '../shared/service/fluxo-service';
import {EtapaConexoes} from '../shared/model/etapa/etapa-conexoes-model';
import {EtapaConexoesCriacao} from '../shared/model/etapa/etapa-conexao-model';
import {EtapaCriacao} from '../shared/model/etapa/etapa-criacao-model';
import {MsgService} from '../shared/service/msg.service';
import {TipoEtapa} from '../shared/enum/tipo-etapa-enum';
import {ChatbotSideNavComponent} from './chatbot-flow-sidenav/chatbot-flow-sidenav-component';
import {FluxoSelect} from '../shared/model/fluxo/fluxo-select-model';
import {EtapaRequisicaoBusca} from '../shared/model/etapa/etapa-requisicao-busca.model';
import {ButtonPadraoComponent} from '../shared/components/button-padrao/button-padrao.component';
import {MatTooltip} from '@angular/material/tooltip';
import {MatDialog} from '@angular/material/dialog';
import {IdNome} from '../shared/model/id-nome.model';
import {TituloRotinaComponent} from '../shared/components/app-titulo/titulo-component';
import {MatIcon} from '@angular/material/icon';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {ValidacaoAlteracaoCampoService} from '../shared/service/utilitarios/validacao-alteracao-campo.service';
import {ModalConfirmacaoComponent} from '../shared/modais/modal-confirmacao/modal-confirmacao.component';
import {CapturaEtapa} from '../shared/model/etapa/etapa-captacao-model';
import {TipoFluxoEnum} from '../shared/enum/tipo-fluxo.enum';
import { EtapaVariavelEnvio } from '../shared/model/etapa/etapa-variavel-envio-model';
import { EtapaAgentAIDifyModel } from '../shared/model/etapa/etapa-agent-ai-dify-model';

@Component({
  selector: 'app-chatbot-flow',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    FFlowModule,
    ButtonPadraoComponent,
    MatTooltip,
    TituloRotinaComponent,
    MatIcon,
    MatProgressSpinnerModule,
  ],
  templateUrl: './chatbot-flow-component.html',
  styleUrls: ['./chatbot-flow-component.css'],
})
export class ChatbotFlowComponent implements OnInit {
  tipoFluxo?: TipoFluxoEnum;
  nodes: FlowNode[] = [];
  connections: FlowConnection[] = [];
  selectedNode: FlowNode | null = null;
  selectedNodeEditando: FlowNode | null = null;
  fluxoId: number | null = null;
  fluxoNome: string | null = null;
  nextConnectionId = 1;
  fluxoUuid: string | null = null;
  carregando = false;

  private static readonly ZOOM_MINIMO = 0.1;
  private static readonly ZOOM_MAXIMO = 2;
  private static readonly ZOOM_FATOR = 1.25;
  private static readonly ZOOM_INTERVALO_MS = 80;
  private static readonly ZOOM_ATRASO_INICIAL_MS = 350;

  private zoomIntervaloId?: ReturnType<typeof setInterval>;
  private zoomAtrasoId?: ReturnType<typeof setTimeout>;

  zoomPercentual = 100;

  @ViewChild(FCanvasComponent) flowCanvasRef?: FCanvasComponent;

  private readonly route = inject(ActivatedRoute);
  private readonly fluxoService = inject(FluxoService);
  private readonly etapaService = inject(EtapaService);
  private readonly msgService = inject(MsgService);
  private readonly dialog = inject(MatDialog);
  private readonly cdr = inject(ChangeDetectorRef);
  readonly alteracaoService = inject(ValidacaoAlteracaoCampoService);

  ngOnInit(): void {
    this.fluxoId = Number(this.route.snapshot.paramMap.get('id'));
    if (this.fluxoId) {
      this.buscarFluxoCompletoPorId(this.fluxoId);
    }
  }

  buscarFluxoCompletoPorId(fluxoId: number) {
    this.carregando = true;
    this.fluxoService.buscarFluxoCompletoPorId(fluxoId).subscribe({
      next: (res) => {
        this.tipoFluxo = res.tipo;
        this.fluxoUuid = res.uuid ?? null;
        this.fluxoNome = res.titulo ? res.titulo + ' ' + `[${this.fluxoUuid}]`  : '';
        if (res.etapas && res.etapas.length > 0) {
          this.nodes = [];
          this.connections = [];

          this.nodes = res.etapas.map((etapa) =>
            this.mapEtapaToFlowNode(etapa)
          );
          this.connections = res.conexoes.map((conn) =>
            this.mapConexaoToFlowConnection(conn)
          );
        } else {
          this.addInitialNode();
        }
      },
      error: () => this.carregando = false,
      complete: () => this.carregando = false,
    });
  }

  getNextNodeId(): number {
    if (this.nodes.length === 0) { return 1; }
    const ids = this.nodes.map((node) => {
      if (node.etapaId != null) { return node.etapaId; }

      const match = node.id.match(/^node-(\d+)$/);
      if (match) { return parseInt(match[1], 10); }
      return 0;
    });
    return Math.max(...ids) + 1;
  }

  addInitialNode() {
    const newId = this.getNextNodeId();
    this.nodes.push(this.createNode(newId, { x: 300, y: 200 }));
  }

  createNode(id: number, position: { x: number; y: number }): FlowNode {
    return {
      id: `node-${id}`,
      etapaId: undefined,
      position,
      content: 'Nova mensagem',
      outputId: `output-node-${id}`,
      inputId: `input-node-${id}`,
      title: 'Nova Etapa',
      description: 'Nova etapa adicionada',
      tipoEtapa: TipoEtapa.TEXTO,
      requisicao: new EtapaRequisicaoBusca(),
      canal: new IdNome(),
      capturas: [],
      tituloOpcao: '',
      descricaoOpcao: '',
      agentAiDify: new EtapaAgentAIDifyModel(),
    };
  }

  temPai(node: FlowNode): boolean {
    const nodeId = this.parseNodeId(node.id);
    return this.connections.some((conn) => conn.targetId === nodeId);
  }

  getNextConnectionId(): string {
    return `conn-${this.nextConnectionId++}`;
  }

  addNodeOnSide(baseNode: FlowNode) {
    let newX = baseNode.position.x + 300;
    const newY = baseNode.position.y;

    const newId = this.getNextNodeId();

    const tipoEtapaBase = String(baseNode.tipoEtapa).toUpperCase();

    const newNode: FlowNode = {
      id: `node-${newId}`,
      position: {
        x:
          tipoEtapaBase === TipoEtapa.LISTA.toString().toUpperCase()
            ? (newX += 10)
            : newX,
        y: newY,
      },
      content:
        tipoEtapaBase === TipoEtapa.LISTA.toString().toUpperCase()
          ? 'Opção da Lista'
          : tipoEtapaBase === TipoEtapa.REQUISICAO_RETORNO.toString().toUpperCase() || tipoEtapaBase === TipoEtapa.REQUISICAO.toString().toUpperCase() ? '' : 'Nova mensagem do bot',
      title: 'Nova Etapa',
      description: 'Nova etapa adicionada',
      outputId: `output-node-${newId}`,
      inputId: `input-node-${newId}`,
      tipoEtapa:
        tipoEtapaBase === TipoEtapa.LISTA.toString().toUpperCase()
          ? TipoEtapa.OPCAO_LISTA
          : TipoEtapa.TEXTO,
      requisicao: new EtapaRequisicaoBusca(),
      agentAiDify: new EtapaAgentAIDifyModel(),
    };

    this.nodes.push(newNode);
    this.connections.push({
      id: `conn-${this.getNextConnectionId()}`,
      sourceId: this.parseNodeId(baseNode.id),
      targetId: this.parseNodeId(newNode.id),
      sourceOutputId: baseNode.outputId,
      targetInputId: newNode.inputId,
    });
  }

  inserirEntrePaiEAtual(targetNode: FlowNode) {
    const targetNodeId = this.parseNodeId(targetNode.id);
    const parentConn = this.connections.find(
      (conn) => conn.targetId === targetNodeId
    );

    if (!parentConn) return;

    const tipoEtapaBase = String(targetNode.tipoEtapa).toUpperCase();

    const sourceNode = this.nodes.find(
      (n) => this.parseNodeId(n.id) === parentConn.sourceId
    );
    if (!sourceNode) return;

    this.connections.splice(
      this.connections.findIndex((conn) => conn.id === parentConn.id),
      1
    );

    const newId = this.getNextNodeId();
    const newNode: FlowNode = {
      id: `node-${newId}`,
      position: {
        x: (sourceNode.position.x + targetNode.position.x) / 2,
        y: (sourceNode.position.y + targetNode.position.y) / 2,
      },
      content: tipoEtapaBase === TipoEtapa.REQUISICAO_RETORNO.toString().toUpperCase() || tipoEtapaBase === TipoEtapa.REQUISICAO.toString().toUpperCase() ? '' : 'Nova mensagem inserida',
      outputId: `output-node-${newId}`,
      inputId: `input-node-${newId}`,
      title: 'Nova Etapa',
      description: 'Nova etapa adicionada',
      tipoEtapa:
        sourceNode.tipoEtapa === TipoEtapa.LISTA
          ? TipoEtapa.OPCAO_LISTA
          : TipoEtapa.TEXTO,
    };

    this.nodes.push(newNode);

    this.connections.push({
      id: this.getNextConnectionId(),
      sourceId: this.parseNodeId(sourceNode.id),
      targetId: this.parseNodeId(newNode.id),
      sourceOutputId: sourceNode.outputId,
      targetInputId: newNode.inputId,
    });

    this.connections.push({
      id: this.getNextConnectionId(),
      sourceId: this.parseNodeId(newNode.id),
      targetId: this.parseNodeId(targetNode.id),
      sourceOutputId: newNode.outputId,
      targetInputId: targetNode.inputId,
    });
  }

  parseNodeId(id: string): number {
    if (!id) return NaN;
    const parsed = id.startsWith('node-') ? id.replace('node-', '') : id;
    return Number(parsed);
  }

  removeNode(nodeToRemove: FlowNode) {
    this.dialog
      .open(ModalConfirmacaoComponent, {
        height: '200px',
        data: { texto: 'Tem certeza que deseja remover esta etapa?' },
      })
      .afterClosed()
      .subscribe({
        next: (res) => {
          if (res) {
            const nodeIndex = this.nodes.findIndex(
              (node) =>
                node.id.replace('node-', '') ===
                nodeToRemove.id.replace('node-', '')
            );

            if (nodeIndex === -1) return;

            const outgoingConnections = this.connections.filter(
              (conn) =>
                conn.sourceId ===
                parseInt(nodeToRemove.id!.replace('node-', ''))
            );
            const incomingConnections = this.connections.filter(
              (conn) => conn.targetInputId === nodeToRemove.inputId
            );

            for (const inConn of incomingConnections) {
              for (const outConn of outgoingConnections) {
                this.connections.push({
                  id: this.generateConnectionId(),
                  sourceId: inConn.sourceId,
                  sourceOutputId: inConn.sourceOutputId,
                  targetInputId: outConn.targetInputId,
                  targetId: outConn.targetId,
                });
              }
            }

            this.nodes.splice(nodeIndex, 1);

            this.connections = this.connections.filter(
              (conn) =>
                conn.sourceId !==
                  parseInt(nodeToRemove.id!.replace('node-', '')) &&
                conn.targetInputId !== nodeToRemove.inputId
            );
          }
        },
      });
  }

  generateConnectionId(): string {
    return 'conn-' + Math.random().toString(36).substr(2, 9);
  }

  onNodeMovedComId(position: { x: number; y: number }, nodeId: string) {
    const node = this.nodes.find((n) => n.id === nodeId);
    if (node) {
      node.position.x = position.x;
      node.position.y = position.y;
    }
  }

  abrirEdicao(node: FlowNode) {
    this.selectedNode = node;
    const dialogRef = this.dialog.open(ChatbotSideNavComponent, {
      width: '361px',
      height: '100vh',
      position: {
        top: '0',
        right: '0',
      },
      data: {
        isUltimo: this.isUltimo(node),
        node: node ? this.clonarNode(node) : null,
        conections: this.buscarConexoesNode(node),
        fluxoId: this.fluxoId ? this.fluxoId : null,
        tipo: this.tipoFluxo ?? TipoFluxoEnum.PADRAO
      },
      panelClass: 'slide-modal',
      disableClose: true,
    });

    dialogRef.backdropClick().subscribe(() => {
      const podeFechar =
        !this.alteracaoService.isCamposAlteracao() ||
        window.confirm('Pode possuir alterações não salvas, deseja fechar?');
      if (podeFechar) {
        dialogRef.close();
      }
    });

    dialogRef.afterClosed().subscribe({
      next: (res) => {
        if (res) {
          res.tipoEtapa = JSON.parse(JSON.stringify(res)).tipoEtapa.value;
          this.selectedNodeEditando = this.clonarNode(res);
          this.salvarEdicao();
        } else {
          this.alteracaoService.camposNaoAlterados();
          this.cancelEdit();
        }
      },
    });
  }

  cancelEdit() {
    this.selectedNode = null;
    this.selectedNodeEditando = null;
  }

  mapEtapaToFlowNode(etapa: EtapaBusca): FlowNode {
    return {
      id: `node-${etapa.id}`,
      etapaId: etapa.id,
      position: { x: etapa.positionX ?? 0, y: etapa.positionY ?? 0 },
      content: etapa.mensagem ?? 'Sem mensagem',
      title: etapa.titulo,
      description: etapa.descricao,
      outputId: etapa.outputId ?? `output-node-${etapa.id}`,
      inputId: etapa.inputId ?? `input-node-${etapa.id}`,
      tipoEtapa: etapa.tipoEtapa,
      requisicao: etapa.requisicao,
      fluxoErro: etapa.fluxoErro,
      fluxoPesquisa: etapa.fluxoPesquisa,
      tituloOpcao: etapa.tituloOpcao,
      descricaoOpcao: etapa.descricaoOpcao,
      canal: etapa.canal ?? undefined,
      capturas: etapa.capturas ?? [],
      variavelRespostaId: etapa.variavelRespostaId ?? undefined,
      metodoValidador: etapa.metodoValidador ?? '',
      variaveisEnvio: etapa.variaveisEnvio ?? [],
      acaoEtapa: etapa.tipoAcao ?? undefined,
      possuiTransbordo: etapa.possuiTransbordo,
      fluxoTransbordo: etapa.fluxoTransbordo,
      timerTransbordo: etapa.timerTransbordo,
      agentAiDify: etapa.etapaAgentAiDify ?? new EtapaAgentAIDifyModel(),
    };
  }

  mapConexaoToFlowConnection(conexao: EtapaConexoes): FlowConnection {
    return {
      id: conexao.id.toString(),
      sourceId: conexao.nodeSourceId,
      targetId: conexao.nodeTargetId,
      sourceOutputId: conexao.outputId,
      targetInputId: conexao.inputId,
    };
  }

  salvarEdicao() {
    if (!this.selectedNode || !this.selectedNodeEditando) { return; }

    const index = this.nodes.findIndex((n) => n.id === this.selectedNode!.id);
    if (index !== -1) {
      if(this.selectedNodeEditando.tipoEtapa === TipoEtapa.REQUISICAO_RETORNO.toString().toUpperCase() || this.selectedNodeEditando.tipoEtapa === TipoEtapa.REQUISICAO.toString().toUpperCase()
          || this.selectedNodeEditando.tipoEtapa?.toString() == "REQUISICAO" || this.selectedNodeEditando.tipoEtapa?.toString() == "REQUISICAO_RETORNO"){
        this.selectedNodeEditando.content = '';
      }
      this.nodes[index] = { ...this.selectedNodeEditando };
    }


    this.selectedNode = null;
    this.selectedNodeEditando = null;
  }

  salvarFluxo() {
    if (!this.fluxoId) {
      this.msgService.msgErro('Fluxo não encontrado!');
      return;
    }
    this.salvarTodasAsEtapas().then((idMap) => {
      this.atualizarConnectionsComIdsReais(idMap);
      this.atualizarEtapasAvancoEPadrao();
      this.salvarTodasAsConexoes();
    });
  }

  async salvarTodasAsEtapas(): Promise<Record<string, number>> {
    this.nodes.forEach((node) => {
      const nodeNum = node.id.replace('node-', '');
      node.inputId = `input-node-${nodeNum}`;
      node.outputId = `output-node-${nodeNum}`;
    });

    const conexoesMap: Record<string, string> = {};
    this.connections.forEach(
      (c) => (conexoesMap[`node-${c.sourceId}`] = `node-${c.targetId}`)
    );

    const etapas: EtapaCriacao[] = this.nodes.map((node) => {
      const targetNodeId = conexoesMap[node.id];
      const targetNode = this.nodes.find((n) => n.id === targetNodeId);
      const conexaoFonte = this.connections.find(
        (c) => `node-${c.targetId}` === node.id
      );
      const sourceNode = conexaoFonte
        ? this.nodes.find((n) => n.id === `node-${conexaoFonte.sourceId}`)
        : null;

      return {
        id: node.etapaId ?? null,
        fluxoId: this.fluxoId!,
        titulo: node.title,
        descricao: node.description,
        mensagem: node.content,
        tituloOpcao: node.tituloOpcao,
        descricaoOpcao: node.descricaoOpcao,
        positionX: node.position.x,
        positionY: node.position.y,
        inputId: node.inputId,
        outputId: node.outputId,
        nodeId: node.id,
        tipoEtapa: node.tipoEtapa,
        etapaAvanco: targetNode?.etapaId,
        etapaPadrao: sourceNode?.etapaId,
        fluxoErro: node.fluxoErro?.id ?? undefined,
        fluxoPesquisa: node.fluxoPesquisa?.id,
        requisicaoSalvamentoDTO: node.requisicao,
        canal: node.canal ?? null,
        capturas: node.capturas ?? null,
        variavelRespostaId: node.variavelRespostaId,
        metodoValidador: node.metodoValidador,
        variaveisEnvio: node.variaveisEnvio ?? [],
        acaoEtapa: node.acaoEtapa,
        possuiTransbordo: node.possuiTransbordo,
        fluxoTransbordo: node.fluxoTransbordo,
        timerTransbordo: node.timerTransbordo,
        agentAiDify: node.agentAiDify ?? null,
      };
    });

    return new Promise<Record<string, number>>((resolve) => {
      this.etapaService.salvarEtapas(etapas).subscribe({
        next: (res) => {
          const idMap: Record<string, number> = {};
          for (const map of res) {
            if (map.nodeIdAntigo && map.etapaId !== undefined) {
              idMap[map.nodeIdAntigo] = map.etapaId;

              const node = this.nodes.find((n) => n.id === map.nodeIdAntigo);
              if (node && map.nodeId) {
                node.etapaId = map.etapaId;
                node.id = map.nodeId;
                node.inputId = `input-node-${map.etapaId}`;
                node.outputId = `output-node-${map.etapaId}`;
              }
            }
          }

          this.nodes.forEach((node) => {
            const conexao = this.connections.find(
              (c) => `node-${c.sourceId}` === node.id
            );
            if (conexao) {
              node.etapaAvanco = idMap[`node-${conexao.targetId}`] ?? null;
            }
          });
          resolve(idMap);
        },
      });
    });
  }

  atualizarConnectionsComIdsReais(idMap: Record<string, number>): void {
    this.connections.forEach((conexao) => {
      const oldSourceKey = `node-${conexao.sourceId}`;
      const oldTargetKey = `node-${conexao.targetId}`;

      const newSourceEtapaId = idMap[oldSourceKey];
      const newTargetEtapaId = idMap[oldTargetKey];

      if (newSourceEtapaId !== undefined) {
        conexao.sourceId = newSourceEtapaId;
        conexao.sourceOutputId = `output-node-${newSourceEtapaId}`;
      }

      if (newTargetEtapaId !== undefined) {
        conexao.targetId = newTargetEtapaId;
        conexao.targetInputId = `input-node-${newTargetEtapaId}`;
      }
    });
  }

  salvarTodasAsConexoes() {
    const conexoes: EtapaConexoesCriacao[] = this.connections
      .filter((c) => c.sourceId && c.targetId)
      .map((c) => ({
        nodeSourceId: c.sourceId!,
        nodeTargetId: c.targetId!,
        inputId: c.targetInputId ?? '',
        outputId: c.sourceOutputId ?? '',
        etapaId: c.sourceId!,
      }));

    this.etapaService.salvarConexoesParaEtapa(conexoes).subscribe({
      next: () => {
        this.alteracaoService.camposNaoAlterados();
        this.msgService.msgSucesso('Fluxo salvo com sucesso.');
        if (this.fluxoId)
          setTimeout(() => this.buscarFluxoCompletoPorId(this.fluxoId!), 0);
      },
    });
  }

  validaRemoveNode(node: FlowNode, index?: number): boolean {
    const tipo = String(node.tipoEtapa).toUpperCase();

    if (index === 0) { return false; }

    const temLigacao = this.connections.some(
      (conn) => conn.sourceId === this.parseNodeId(node.id)
    );

    if (tipo === 'LISTA' && temLigacao) { return false; }

    return true;
  }

  isUltimo(node: FlowNode): boolean {
    return (
      this.connections.find(
        (conn) => conn.sourceId === parseInt(node.id!.replace('node-', ''))
      ) === undefined
    );
  }

  verificaAcaoEncerramento(node: FlowNode): boolean {
    return node.acaoEtapa === 'ENCERRAR_SESSAO' || node.acaoEtapa === 'Encerrar Sessão';
  }


  atualizarEtapasAvancoEPadrao() {
    const atualizacoes: {
      etapaId: number;
      etapaAvanco?: number;
      etapaPadrao?: number;
    }[] = [];

    this.connections.forEach((connection) => {
      const etapaIdSource = connection.sourceId;
      const etapaIdTarget = connection.targetId;

      if (etapaIdSource && etapaIdTarget) {
        atualizacoes.push({
          etapaId: etapaIdSource,
          etapaAvanco: etapaIdTarget,
        });

        atualizacoes.push({
          etapaId: etapaIdTarget,
          etapaPadrao: etapaIdSource,
        });
      }
    });
    this.etapaService.atualizarEtapasAvancoEPadrao(atualizacoes).subscribe();
  }

  clonarNode(node: FlowNode): FlowNode {
    return JSON.parse(JSON.stringify(node));
  }

  podeCriarAvanco(node: FlowNode) {
    const tipo =
      typeof node.tipoEtapa === 'string'
        ? node.tipoEtapa.toUpperCase()
        : node.tipoEtapa;

    return (this.isUltimo(node) || tipo === TipoEtapa.LISTA || tipo === 'LISTA') && (tipo !== 'CANAL' && tipo !== TipoEtapa.CANAL) && (!this.verificaAcaoEncerramento(node));
  }

  getIconTipo(tipo: TipoEtapa | string): string {
    switch (tipo) {
      case TipoEtapa.TEXTO:
      case 'TEXTO':
        return './assets/icones/texto';
      case TipoEtapa.ENTRADA:
      case 'ENTRADA':
        return './assets/icones/entrada';
      case TipoEtapa.LISTA:
      case 'LISTA':
        return './assets/icones/lista';
      case TipoEtapa.OPCAO_LISTA:
      case 'OPCAO_LISTA':
        return './assets/icones/opcao-lista';
      case TipoEtapa.REQUISICAO_RETORNO:
      case 'REQUISICAO_RETORNO':
        return './assets/icones/requisicao-retorno';
      case TipoEtapa.REQUISICAO:
      case 'REQUISICAO':
        return './assets/icones/requisicao';
      case TipoEtapa.TEXTO_VARIAVEIS:
      case 'TEXTO_VARIAVEIS':
        return './assets/icones/texto-variavel';
      case TipoEtapa.CANAL:
      case 'CANAL':
        return './assets/icones/canal';
      case TipoEtapa.CAPTURA:
      case 'CAPTURA':
        return './assets/icones/captura';
      case TipoEtapa.ACAO:
      case 'ACAO':
        return './assets/icones/acao';
      case TipoEtapa.AGENTE_IA_DIFY:
      case 'AGENTE_IA_DIFY':
        return './assets/icones/dify_image';
      default:
        break;
    }
    return './assets/icones/no-img-default';
  }

  getLabel(tipo: TipoEtapa | string): string {
    switch (tipo) {
      case TipoEtapa.TEXTO:
      case 'TEXTO':
        return 'Texto';
      case TipoEtapa.ENTRADA:
      case 'ENTRADA':
        return 'Entrada de Varíavel';
      case TipoEtapa.LISTA:
      case 'LISTA':
        return 'Lista';
      case TipoEtapa.OPCAO_LISTA:
      case 'OPCAO_LISTA':
        return 'Opção da Lista';
      case TipoEtapa.REQUISICAO_RETORNO:
      case 'REQUISICAO_RETORNO':
        return 'Requisição com retorno';
      case TipoEtapa.REQUISICAO:
      case 'REQUISICAO':
        return 'Requisição';
      case TipoEtapa.TEXTO_VARIAVEIS:
      case 'TEXTO_VARIAVEIS':
        return 'Texto com variáveis';
      case TipoEtapa.CANAL:
      case 'CANAL':
        return 'Canal';
      case TipoEtapa.CAPTURA:
      case 'CAPTURA':
        return 'Captura';
      case TipoEtapa.ACAO:
      case 'AÇAO':
        return 'Ação';
      case TipoEtapa.AGENTE_IA_DIFY:
      case 'AGENTE_IA_DIFY':
        return 'Agent AI (Dify)';
      default:
        return tipo;
    }
  }

  buscarConexoesNode(node:FlowNode):FlowConnection[] {
    return this.connections.filter(
      (conn) => conn.sourceId === this.parseNodeId(node.id)
    );
  }

  aumentarZoom(): void {
    const canvas = this.flowCanvasRef;
    if (!canvas) { return; }
    const novaEscala = Math.min(ChatbotFlowComponent.ZOOM_MAXIMO, canvas.getScale() * ChatbotFlowComponent.ZOOM_FATOR);
    canvas.setScale(novaEscala);
    canvas.redraw();
    this.atualizarZoomPercentual(novaEscala);
  }

  diminuirZoom(): void {
    const canvas = this.flowCanvasRef;
    if (!canvas) { return; }
    const novaEscala = Math.max(ChatbotFlowComponent.ZOOM_MINIMO, canvas.getScale() / ChatbotFlowComponent.ZOOM_FATOR);
    canvas.setScale(novaEscala);
    canvas.redraw();
    this.atualizarZoomPercentual(novaEscala);
  }

  onCanvasChange(event: FCanvasChangeEvent): void {
    this.atualizarZoomPercentual(event.scale);
  }

  private atualizarZoomPercentual(escala: number): void {
    this.zoomPercentual = Math.round(escala * 100);
    this.cdr.detectChanges();
  }

  iniciarZoomInContinuo(): void {
    this.iniciarZoomContinuo(() => this.aumentarZoom());
  }

  iniciarZoomOutContinuo(): void {
    this.iniciarZoomContinuo(() => this.diminuirZoom());
  }

  private iniciarZoomContinuo(acao: () => void): void {
    this.pararZoomContinuo();
    acao();
    this.zoomAtrasoId = setTimeout(() => {
      this.zoomIntervaloId = setInterval(acao, ChatbotFlowComponent.ZOOM_INTERVALO_MS);
    }, ChatbotFlowComponent.ZOOM_ATRASO_INICIAL_MS);
  }

  pararZoomContinuo(): void {
    if (this.zoomAtrasoId) {
      clearTimeout(this.zoomAtrasoId);
      this.zoomAtrasoId = undefined;
    }
    if (this.zoomIntervaloId) {
      clearInterval(this.zoomIntervaloId);
      this.zoomIntervaloId = undefined;
    }
  }

  copiarUUID(){
    navigator.clipboard.writeText('[' + this.fluxoUuid + ']').then(() => {
      this.msgService.msgSucesso('UUID copiado com sucesso!');
    })
  }
}

export interface FlowNode {
  id: string;
  etapaId?: number;
  etapaAvanco?: number;
  fluxoErro?: FluxoSelect;
  fluxoPesquisa?: FluxoSelect;
  position: { x: number; y: number };
  content: string;
  title?: string;
  description?: string;
  outputId?: string;
  inputId?: string;
  tipoEtapa?: TipoEtapa;
  requisicao?: EtapaRequisicaoBusca;
  canal?: IdNome;
  capturas?: CapturaEtapa[];
  tituloOpcao?: string;
  descricaoOpcao?: string;
  variavelRespostaId?: number;
  metodoValidador?: string;
  acaoEtapa?: string;
  variaveisEnvio?: EtapaVariavelEnvio[];
  possuiTransbordo?: boolean;
  fluxoTransbordo?: number;
  timerTransbordo?: number;
  agentAiDify?: EtapaAgentAIDifyModel;
}

export interface FlowConnection {
  id: string;
  sourceId?: number;
  targetId?: number;
  sourceOutputId?: string;
  targetInputId?: string;
}
